<template>
  <div class='collapsible-tidy-tree' id='tree-content'>
    <svg id='tree-content-svg' :width='width' :height='height'></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';
import _ from 'lodash';

export default {
  name: 'CollapsibleTidyTree',
  props: {
    data: {
      type: Object,
      required: true,
    },
    width: {
      default: 1100,
      type: Number,
    },
    height: {
      default: 700,
      type: Number,
    },
    offsetTop: {
      default: 20,
      type: Number,
    },
    offsetLeft: {
      default: 190,
      type: Number,
    },
    offsetRight: {
      default: 90,
      type: Number,
    },
    offsetBottom: {
      default: 30,
      type: Number,
    },
    featureLinks: {
      type: Array,
      required: false,
      default: () => [],
    },
    existFeatureLinks: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      treemap: null,
      objectsGroup: null,
      duration: 750,
      root: null,
      i: 0,
      zoom: null,
    };
  },
  mounted() {
    if (!_.isEmpty(this.data)) {
      this.buildChart();
    }
  },

  methods: {
    buildChart() {
      // Set the dimensions and margins of the diagram
      const width = this.width - this.offsetLeft - this.offsetRight;
      const height = this.height - this.offsetTop - this.offsetBottom;

      const svg = d3.select('#tree-content-svg');
      svg.selectAll('*').remove();
      this.objectsGroup = svg.append('g').attr('transform', `translate(${ this.offsetLeft },${ this.offsetTop })`);

      // declares a tree layout and assigns the size
      this.treemap = d3.tree().size([height, width]);

      // Assigns parent, children, height, depth
      this.root = d3.hierarchy(this.data, d => d.children);
      this.root.x0 = height / 2;
      this.root.y0 = 0;

      // Collapse after the second level
      this.root.children.forEach(this.collapse);

      // create zoom
      this.zoom = d3.zoom()
        .scaleExtent([0.1, 3])
        .on('zoom', this.handleZoom);

      this.data.children.forEach(item => {
        this.setFeatures(item);
      });
      svg.selectAll('text')
        .data(this.data)
        .join(enter => {
          const g = enter.append('g');
          g.append('text')
            .text(d => (d.data.features));
        });
      svg.call(this.zoom);
      this.updateTree(this.root);
      this.centerNode(this.root);
    },
    // zoom and pan handler
    handleZoom(e) {
      this.objectsGroup
        .attr('transform', e.transform);
    },
    // Collapse the node and all it's children
    collapse(d) {
      if (d.children) {
        // eslint-disable-next-line no-param-reassign,no-underscore-dangle
        d._children = d.children;
        // eslint-disable-next-line no-underscore-dangle
        d._children.forEach(this.collapse);
        // eslint-disable-next-line no-param-reassign
        d.children = null;
      }
    },

    updateTree(source) {
      // Compute the new height, function counts total children of root node and sets tree height accordingly.
      // This prevents the layout looking squashed when new nodes are made visible or looking sparse when nodes are removed
      // This makes the layout more consistent.
      const levelWidth = [1];
      const childCount = (level, n) => {
        if (n.children && n.children.length > 0) {
          if (levelWidth.length <= level + 1) levelWidth.push(0);

          levelWidth[level + 1] += n.children.length;
          n.children.forEach(d => {
            childCount(level + 1, d);
          });
        }
      };
      childCount(0, this.root);
      const width = this.width - this.offsetLeft - this.offsetRight;
      const height = this.height - this.offsetTop - this.offsetBottom;
      const maxLevelWidth = d3.max(levelWidth);
      const newHeight = maxLevelWidth * d3.max([height / maxLevelWidth, 25]); // calculate pixels per line
      this.treemap = this.treemap.size([newHeight, width]);
      // Assigns the x and y position for the nodes
      const treeData = this.treemap(this.root);

      // Compute the new tree layout.
      const nodes = treeData.descendants();
      const links = treeData.descendants().slice(1);

      // Normalize for fixed-depth.
      nodes.forEach(d => {
        // eslint-disable-next-line no-param-reassign
        d.y = d.depth * 180;
        // eslint-disable-next-line no-param-reassign
        d.x *= nodes.length >= 15 ? 2 : 1;
      });

      // ****************** Nodes section ***************************

      // Update the nodes...
      const node = this.objectsGroup.selectAll('g.node')
        .data(nodes, d => {
          if (!d.id) {
            this.i += 1;
            // eslint-disable-next-line no-param-reassign
            d.id = this.i;
          }
          return d.id;
        });

      // Enter any new modes at the parent's previous position.
      const nodeEnter = node.enter().append('g')
        .attr('class', 'node')
        .attr('transform', () => `translate(${ source.y0 || source.y },${ source.x0 || source.x })`)
        .on('click', this.click);

      // Add Circle for the nodes
      nodeEnter.append('circle')
        .attr('class', 'node')
        .attr('r', 1e-6)
        // eslint-disable-next-line no-underscore-dangle
        .style('fill', d => (d._children ? '#EAF8F7' : '#fff'));

      // Add link button for the nodes with links
      nodeEnter.append('path')
        // eslint-disable-next-line max-len
        .attr('d', 'M15.197 3.35462C16.8703 1.67483 19.4476 1.53865 20.9536 3.05046C22.4596 4.56228 22.3239 7.14956 20.6506 8.82935L18.2268 11.2626M10.0464 14C8.54044 12.4882 8.67609 9.90087 10.3494 8.22108L12.5 6.06212M13.9536 10C15.4596 11.5118 15.3239 14.0991 13.6506 15.7789L11.2268 18.2121L8.80299 20.6454C7.12969 22.3252 4.55237 22.4613 3.0464 20.9495C1.54043 19.4377 1.67609 16.8504 3.34939 15.1706L5.77323 12.7373')
        .attr('cursor', 'pointer')
        // eslint-disable-next-line no-underscore-dangle
        .attr('transform', d => (d.children || d._children ? 'translate(-30, -8) scale(0.7)' : 'translate(13, -8) scale(0.7)'))
        .style('stroke', '#B331CF')
        .style('stroke-width', 1.5)
        .style('stroke-linecap', 'round')
        .style('fill', 'transparent')
        .style('display', d => (d.data.link ? 'block' : 'none'))
        .on('click', (event, d) => {
          event.stopPropagation();
          if (d.data.link) {
            window.open(d.data.link);
          }
        });
      // Add labels for the nodes
      nodeEnter.append('text')
        .attr('dy', '.35em')
        // eslint-disable-next-line no-underscore-dangle,no-nested-ternary
        .attr('x', d => ((d.children || d._children) && !d.data.link ? -16 : d.children || d._children ? -33 : 33))
        // eslint-disable-next-line no-underscore-dangle
        .attr('text-anchor', d => (d.children || d._children ? 'end' : 'start'))
        // eslint-disable-next-line no-underscore-dangle
        .attr('cursor', d => (d.children || d._children ? 'pointer' : 'default'))
        .text(d => d.data.name);

      // Update
      const nodeUpdate = nodeEnter.merge(node);
      // Add badge for the features
      const featuresGroup = nodeUpdate.filter(d => d.data.features && d.data.features.length > 0)
        .append('g')
        .attr('class', 'feature-group');
      featuresGroup.each(function func(d) {
        const featureGroup = d3.select(this);
        d.data.features.forEach((feature, index) => {
          const rectWidth = 120;
          const rectHeight = 15;

          featureGroup.append('rect')
            // eslint-disable-next-line no-underscore-dangle
            .attr('x', (d.children || d._children) ? -153 - (125 * index) : 33 + (125 * index))
            .attr('y', 9)
            .attr('width', rectWidth)
            .attr('height', rectHeight)
            .attr('rx', 4)
            .attr('ry', 4)
            .style('fill', '#EDF5FD');

          featureGroup.append('text')
            // eslint-disable-next-line no-underscore-dangle
            .attr('x', (d.children || d._children) ? -153 - (125 * index) + rectWidth / 2 : 33 + (125 * index) + rectWidth / 2)
            .attr('y', 16)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .style('fill', '#4d9feb')
            .style('font-size', '12px')
            .style('user-select', 'none')
            .style('-webkit-user-select', 'none')
            .style('-ms-user-select', 'none')
            .text(feature);
        });
      });

      // Transition to the proper position for the node
      nodeUpdate.transition()
        .duration(this.duration)
        .attr('transform', d => `translate(${ d.y },${ d.x })`);

      // Update the node attributes and style
      nodeUpdate.select('circle.node')
        .attr('r', 10)
        // eslint-disable-next-line no-underscore-dangle
        .style('fill', d => (d._children ? '#EAF8F7' : '#fff'))
        .attr('cursor', 'pointer');

      // Remove any exiting nodes
      const nodeExit = node.exit().transition()
        .duration(this.duration)
        .attr('transform', () => `translate(${source.y},${source.x})`)
        .remove();

      // On exit reduce the node circles size to 0
      nodeExit.select('circle')
        .attr('r', 1e-6);

      // On exit reduce the opacity of text labels
      nodeExit.select('text')
        .style('fill-opacity', 1e-6);

      // ****************** links section ***************************

      // Update the links...
      const link = this.objectsGroup.selectAll('path.link')
        .data(links, d => d.id);

      // Enter any new links at the parent's previous position.
      const linkEnter = link.enter().insert('path', 'g')
        .attr('class', 'link')
        .attr('d', () => {
          const o = { x: source.x0 || source.x, y: source.y0 || source.y };
          return this.diagonal(o, o);
        });

      // Update
      const linkUpdate = linkEnter.merge(link);

      // Transition back to the parent element position
      linkUpdate.transition()
        .duration(this.duration)
        .attr('d', d => this.diagonal(d, d.parent));

      // Remove any exiting links
      // eslint-disable-next-line no-unused-vars
      const linkExit = link.exit().transition()
        .duration(this.duration)
        .attr('d', () => {
          const o = { x: source.x, y: source.y };
          return this.diagonal(o, o);
        })
        .remove();

      // Store the old positions for transition.
      nodes.forEach(d => {
        // eslint-disable-next-line no-param-reassign
        d.x0 = d.x;
        // eslint-disable-next-line no-param-reassign
        d.y0 = d.y;
      });
    },

    // Creates a curved (diagonal) path from parent to the child nodes
    diagonal(s, d) {
      const path = `M ${s.y} ${s.x}
              C ${(s.y + d.y) / 2} ${s.x},
                ${(s.y + d.y) / 2} ${d.x},
                ${d.y} ${d.x}`;

      return path;
    },
    setFeatures(item) {
      if (item.link) {
        const features = this.getFeature(item.link);
        if (features.length > 0) {
          // eslint-disable-next-line no-param-reassign
          item.features = features;
        }
      }
      if (item.children) {
        item.children.forEach(this.setFeatures);
      }
    },
    getFeature(link) {
      const result = [];
      const linksPerFeatures = this.existFeatureLinks;
      Object.keys(linksPerFeatures).forEach(key => {
        if (linksPerFeatures[key].includes(link)) {
          result.push(this.$t(`feature.title.${key}`));
        }
      });
      return result;
    },

    // Toggle children on click.
    click(event, d) {
      if (d.children) {
        // eslint-disable-next-line no-param-reassign,no-underscore-dangle
        d._children = d.children;
        // eslint-disable-next-line no-param-reassign
        d.children = null;
      } else {
        // eslint-disable-next-line no-param-reassign,no-underscore-dangle
        d.children = d._children;
        // eslint-disable-next-line no-param-reassign,no-underscore-dangle
        d._children = null;
      }
      this.updateTree(d);
      this.centerNode(d);
    },
    centerNode(d) {
      d3.select('#tree-content-svg')
        .transition()
        .call(this.zoom.translateTo, d.y, d.x);
    },

    expandPathToNode(rootNode, path) {
      let currentNode = rootNode;
      const pathParts = path.split(this.data.link)[1].split('/').filter(part => part.trim() !== '');
      // eslint-disable-next-line no-restricted-syntax
      for (const part of pathParts) {
        if (currentNode.children) {
          const childNode = currentNode.children.find(child => child.data.name === part);
          if (childNode) {
            currentNode = childNode;
            // eslint-disable-next-line no-underscore-dangle
            if (currentNode._children && pathParts.indexOf(part) !== pathParts.length - 1) {
              // eslint-disable-next-line no-underscore-dangle
              currentNode.children = currentNode._children;
              // eslint-disable-next-line no-underscore-dangle
              currentNode._children = null;
            }
          } else {
            break;
          }
        }
      }
      this.updateTree(currentNode);
      this.centerNode(currentNode);
    },
    checkIfReopenPaths(newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        return true;
      }
      newVal.sort();
      oldVal.sort();
      for (let i = 0; i < newVal.length; i += 1) {
        if (newVal[i] !== oldVal[i]) {
          return true;
        }
      }
      return false;
    },
  },
  watch: {
    data: {
      async handler() {
        if (!_.isEmpty(this.data)) {
          this.treemap = null;
          this.objectsGroup = null;
          this.root = null;
          this.i = 0;
          this.zoom = null;
          this.buildChart();
        }
      },
      deep: true,
    },
    featureLinks: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          const reopenPaths = this.checkIfReopenPaths(newVal, oldVal);
          if (reopenPaths) {
            this.root.children.forEach(this.collapse);
            this.updateTree(this.root);
            this.featureLinks.forEach(path => {
              this.expandPathToNode(this.root, path);
            });
          }
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.collapsible-tidy-tree {
  .node circle {
    fill: var(--bg-primary);
    stroke: var(--status-success);
    stroke-width: 2px;
  }

  .node text {
    font: $font-size-sm $font-family-regular;
  }

  .link {
    fill: none;
    stroke: var(--text-tertiary);
    stroke-width: 1px;
  }
}
</style>
